import { useMemo } from "react";
import {
    DocumentText,
    Eye,
    Link1,
    LoginCurve,
    TickCircle,
    Timer1,
} from "iconsax-react";
import { useTranslation } from "react-i18next";
import {
    Bar,
    BarChart,
    CartesianGrid,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import { formatCount, monthLabel, statusKey, typeKey } from "./helpers";

const KPI_METRICS = [
    { key: "contents", valueKey: "contents", icon: DocumentText },
    { key: "published", valueKey: "published", icon: TickCircle },
    { key: "pending", valueKey: "pending", icon: Timer1 },
    { key: "views", valueKey: "views", icon: Eye },
    { key: "shares", valueKey: "link_shares", icon: Link1 },
    { key: "opens", valueKey: "qualified_opens", icon: LoginCurve },
];

export default function ContentAnalytics({ data, locale, loading, error, onRetry }) {
    const { t } = useTranslation();
    const totals = data?.totals || {};
    const chartData = useMemo(
        () =>
            (data?.monthly_views || []).map((row) => ({
                name: monthLabel(row.year, row.month, locale),
                views: row.views,
            })),
        [data, locale]
    );

    if (loading) {
        return (
            <div className="studio-state" role="status" aria-live="polite">
                <span className="studio-spinner" aria-hidden="true" />
                {t("contentStudio.loading")}
            </div>
        );
    }
    if (error) {
        return (
            <div className="studio-state studio-state-error" role="alert">
                <p>{error}</p>
                <button type="button" onClick={onRetry}>
                    {t("contentStudio.retry")}
                </button>
            </div>
        );
    }

    return (
        <div className="studio-analytics">
            <section
                className="studio-kpi-strip"
                aria-label={t("contentStudio.analytics.kpis")}
            >
                {KPI_METRICS.map((metric) => {
                    const Icon = metric.icon;
                    return (
                        <div key={metric.key}>
                            <Icon size={16} aria-hidden="true" />
                            <div>
                                <span>
                                    {t(`contentStudio.analytics.${metric.key}`)}
                                </span>
                                <strong>
                                    {formatCount(totals[metric.valueKey], locale)}
                                </strong>
                            </div>
                        </div>
                    );
                })}
            </section>
            <div className="studio-analytics-grid">
                <section className="studio-panel">
                    <h2>{t("contentStudio.analytics.viewsOverTime")}</h2>
                    {chartData.length ? (
                        <div className="studio-chart">
                            <ResponsiveContainer width="100%" height={240}>
                                <BarChart data={chartData}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="name" />
                                    <YAxis allowDecimals={false} />
                                    <Tooltip />
                                    <Bar dataKey="views" fill="#2e71fc" radius={[4, 4, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    ) : (
                        <p className="studio-muted">
                            {t("contentStudio.analytics.noViews")}
                        </p>
                    )}
                </section>
                <section className="studio-panel">
                    <h2>{t("contentStudio.analytics.top")}</h2>
                    {(data?.top || []).length ? (
                        <table className="studio-table">
                            <thead>
                                <tr>
                                    <th>{t("contentStudio.fields.title")}</th>
                                    <th>{t("contentStudio.analytics.views")}</th>
                                    <th>{t("contentStudio.analytics.shares")}</th>
                                    <th>{t("contentStudio.filters.status")}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(data.top || []).map((row) => (
                                    <tr key={row.id}>
                                        <td>
                                            <strong>{row.name}</strong>
                                            <small>
                                                {t(
                                                    `contentStudio.type.${typeKey(
                                                        row.content_type
                                                    )}`
                                                )}
                                            </small>
                                        </td>
                                        <td className="tabular">
                                            {formatCount(row.views_count, locale)}
                                        </td>
                                        <td className="tabular">
                                            {formatCount(row.share_links, locale)}
                                        </td>
                                        <td>
                                            {t(
                                                `contentStudio.status.${statusKey(
                                                    row.status
                                                )}`
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    ) : (
                        <p className="studio-muted">{t("contentStudio.empty")}</p>
                    )}
                </section>
            </div>
        </div>
    );
}
